import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiGet } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { BulkActionJob } from '@/types/crm';
import { TERMINAL_BULK_ACTION_JOB_STATUSES } from '@/types/crm';

export interface UseBulkActionJobResult {
  job: BulkActionJob | null;
  isLoading: boolean;
  error: 'not_found' | 'network' | null;
}

export function useBulkActionJob(
  orgId: number,
  jobId: number | null
): UseBulkActionJobResult {
  const enabled = orgId > 0 && jobId !== null;

  const { data, isLoading, error } = useQuery<BulkActionJob, Error>({
    queryKey: ['bulk-action-job', orgId, jobId],
    queryFn: async () => {
      try {
        return await apiGet<BulkActionJob>(
          `/api/orgs/${orgId}/crm-events/bulk-action/jobs/${jobId}/`
        );
      } catch (err) {
        const { statusCode } = err as { statusCode?: number };
        if (statusCode !== 404) {
          logger.warn('useBulkActionJob: fetch failed', {
            component: 'crm-bulk-action',
            org_id: orgId,
            bulk_action_job_id: jobId,
            status_code: statusCode,
          });
        }
        throw err;
      }
    },
    enabled,
    staleTime: 0,
    retry: false,
    refetchInterval: query => {
      const result = query.state.data;
      if (!result) {
        return 2000;
      }
      if (TERMINAL_BULK_ACTION_JOB_STATUSES.has(result.status)) {
        return false;
      }
      return 2000;
    },
  });

  const errorKind = useMemo<UseBulkActionJobResult['error']>(() => {
    if (!error) {
      return null;
    }
    const { statusCode } = error as { statusCode?: number };
    if (statusCode === 404) {
      return 'not_found';
    }
    return 'network';
  }, [error]);

  return useMemo(
    () => ({ job: data ?? null, isLoading, error: errorKind }),
    [data, isLoading, errorKind]
  );
}
